// src/utils/baselineUtils.js

/**
 * Returns a new baseline object, safely adding derived properties.
 * @param {Object} baseline
 * @returns {Object} Extended baseline object
 */
import {
	filterObjectsByProperty,
	getValuesByPropertyPath,
	findObjectByProperty,
	deepClone,
} from "./dataUtils";
import { evaluateAccessLevel } from "./accessControlUtils";

export const getBaselineItemDataStatusLabel = (dataStatusId) => {
	switch (Number(dataStatusId)) {
		case 1:
			return "Ready";
		case 2:
			return "Partial";
		case 3:
			return "Unresolved";
		case 4:
			return "Reviewed";
		default:
			return "Unknown";
	}
};

/**
 * Prepares a baseline object by deep-cloning it and enriching it with config arrays for UI usage.
 * - Adds cfg_* properties for baseline configuration and access control.
 * - Resets the update_flag for all baseline accesses.
 * - Preserves the previous number of periods.
 *
 * @param {Object} baseline - The baseline object to process.
 * @returns {Object} The enriched, cloned baseline object.
 *
 * @example
 * const readyBaseline = prepareBaselineObject(rawBaseline);
 * // readyBaseline.cfg_period_label, readyBaseline.cfg_edit_access_group, etc. will be available
 */
export const prepareBaselineObject = (baseline) => {
	if (!baseline || typeof baseline !== "object") return {};

	// creates a shallow copy of the top-level object (baseline)
	// Note: It does not deeply clone any of the nested arrays or objects—such as data.baseline_accesses or data.baseline_configs. Those arrays are still references to the original arrays in your Redux store or wherever the original baseline came from

	// Deep clone to avoid mutating original baseline data
	const data = deepClone(baseline);

	const lookups = JSON.parse(sessionStorage.getItem("lookups")) || {};

	// Add cfg_* config arrays for each baseline config type
	(lookups.BL_CFG_TYPES || []).forEach((c) => {
		const configs = Array.isArray(data.baseline_configs)
			? data.baseline_configs.filter((x) => x.comp_type_id === c.id)
			: [];
		let sorted = configs.sort((a, b) => a.sort_id - b.sort_id);
		if (c.format === "currency") {
			sorted = sorted.map((x) => ({ ...x, currency_unit: data.currency_unit }));
		}
		data[`cfg_${c.code}`] = sorted;
	});

	// Reset update_flag on baseline accesses
	(Array.isArray(data.baseline_accesses) ? data.baseline_accesses : []).forEach((x) => {
		x.update_flag = 0;
	});

	// Add cfg_* access arrays for each access control type
	(lookups.BL_CFG_ACCESSES || []).forEach((c) => {
		const accesses = Array.isArray(data.baseline_accesses)
			? data.baseline_accesses.filter((x) => x.comp_type_id === c.id)
			: [];
		const sorted = accesses.sort((a, b) => a.sort_id - b.sort_id);
		data[`cfg_${c.code}`] = sorted;
	});

	// Save the previous number of periods for tracking changes
	data.number_of_period_prev = data.number_of_period;

	data.number_of_active_items = data.baseline_items
		? data.baseline_items.filter((item) => item.status_id === 1).length
		: 0;

	return {
		...data,
		statusLabel: data.status_id === 1 ? "Active" : data.status_id === 0 ? "Archived" : "Unknown",
	};
};

/**
 * Enriches a baseline item with related config, options, and force structure data for editing.
 * - Deep clones the item and baseline to prevent mutation of original data.
 * - Populates options and force structure details on the item.
 * - Moves the item to the front of the baseline_items array.
 * - Adds auxiliary fields for editing (option_type_id_prev, update_flag, number_of_period).
 *
 * @param {Object} baseline - The baseline object containing baseline_items and config arrays.
 * @param {Object} item - The baseline item object to enrich.
 * @param {Object} lookups - Lookup tables (must include BL_LOOKUPS_ITEM_OPTIONS).
 * @returns {Object} The enriched baseline item object, or empty object if invalid input.
 *
 * @example
 * const updatedItem = prepareBaselineItemObject(baseline, item, lookups);
 * // updatedItem.options, updatedItem.forceStructures, etc. now available
 */
export const prepareBaselineItemObject = (baseline, item, lookups) => {
	//  Check required arguments first
	if (!baseline || !item || !lookups) {
		console.error("prepareBaselineItemObject: Missing required argument(s)", {
			baseline,
			item,
			lookups,
		});
		return {};
	}
	if (!("option_type_id" in item)) {
		console.error("prepareBaselineItemObject: item is missing option_type_id", item);
		return {};
	}

	// Deep clone to avoid mutating originals
	const cloneItem = deepClone(item);
	const cloneBaseline = deepClone(baseline);

	// Store previous option_type_id for UI
	cloneItem.option_type_id_prev = cloneItem.option_type_id;

	// Lookup and attach valid item options for this item type
	const options = filterObjectsByProperty(
		lookups.BL_LOOKUPS_ITEM_OPTIONS,
		"group_id",
		cloneItem.option_type_id,
	);
	cloneItem.options = options;
	cloneItem.optionIds = getValuesByPropertyPath(options, "lookup_id");

	// Move this item to the front of baseline_items
	cloneBaseline.baseline_items = cloneBaseline.baseline_items.filter((x) => {
		return x.id !== item.id;
	});
	cloneBaseline.baseline_items.unshift(cloneItem); // add the item to the beginning of the array

	// Attach forceStructures: filter and sort relevant configs
	const forceStructureIds = getValuesByPropertyPath(cloneBaseline.cfg_force_structure_metrics, "id");
	cloneItem.forceStructures = cloneItem.baseline_item_configs
		.filter((x) => forceStructureIds.indexOf(x.comp_type_id) !== -1)
		.sort((x, y) => x.sort_id - y.sort_id);

	// Attach label/color to each forceStructure
	cloneItem.forceStructures.map((x) => {
		const o = findObjectByProperty(cloneBaseline.baseline_configs, "id", x.comp_type_id);
		x.label = o.comp_value;
		x.color = o.color;
		return false;
	});

	cloneItem.update_flag = 0;
	cloneItem.number_of_period = cloneBaseline.number_of_period;

	return cloneItem;
};

// Utility to safely clone and augment baseline objects

// helper: build a human readable label, e.g. "FY2026 – FY2031 (6)"
const buildPeriodLabel = (periods) => {
	if (!Array.isArray(periods) || periods.length === 0) return "N/A";
	const first = periods[0];
	const last = periods[periods.length - 1];
	// Tweak this format to match your display convention
	return `${first} - ${last} (${periods.length})`;
};

const toTS = (v) => {
	if (v == null) return 0;
	const n = Date.parse(v);
	return Number.isFinite(n) ? n : 0;
};

// returns a NEW baseline object with derived fields (no mutation)
export const extendBaseline = (baseline) => {
	if (!baseline || typeof baseline !== "object") return baseline;

	// If already extended, return a stable object to avoid churn
	if (baseline.periodLabel) return baseline;

	// Periods are provided by baseline configs (comp_type_id 11) in your utils
	// (prepareScenarioObject already reads them as "periods"/labels)  // see scenarioUtils. :contentReference[oaicite:2]{index=2}
	const periods = Array.isArray(baseline.baseline_configs)
		? baseline.baseline_configs
				.filter((c) => c.comp_type_id === 11)
				.sort((a, b) => (a.sort_id ?? 0) - (b.sort_id ?? 0))
				.map((c) => c.comp_value)
		: [];

	const periodLabel = buildPeriodLabel(periods);

	const number_of_active_items = baseline.baseline_items
		? baseline.baseline_items.filter((item) => item.status_id === 1).length
		: 0;

	// Keep currency props handy (you already render currency fields in ScenarioList)
	const next = {
		...baseline,
		periodLabel,
		number_of_active_items: number_of_active_items,
		currency_code: baseline.currency_code,
		currency_symbol: baseline.currency_symbol,
		currency_unit: baseline.currency_unit,
		createdAtTS: toTS(baseline.createdAt),
		updatedAtTS: toTS(baseline.updatedAt),
	};

	return next;
};

/**
 * Determine a user's access level to a baseline.
 * Returns 2 for edit, 1 for read, 0 for none.
 *
 * @param {Object} item - The baseline object with baseline_accesses
 * @param {Array} groups - The user group list
 * @param {Object} user - The user object (should have .id)
 *
 * Special group IDs:
 *  -100: "Everyone"
 *  -99:  "All logged-in users"
 */
// Keep Baseline callers on their existing export while delegating the shared ACL logic.
export const baselineAccess = (item, groups, user) =>
	evaluateAccessLevel(item, item?.baseline_accesses, groups, user, {
		publicLogMessage: "[baselineAccess] PUBLIC → READ",
		allLoginLogMessage: "[baselineAccess] ALL_LOGIN",
		logIdKey: "baselineId",
	});

/*
 * Disabled historical reference only; this block has no runtime effect.
 * The active Baseline and Scenario access paths delegate to
 * accessControlUtils.evaluateAccessLevel.
 * It can be removed once the shared implementation no longer needs this reference.
 */
/* item.baseline_accesses.forEach((r) => {
		const acl = findObjectByProperty(ACLs, "id", r.comp_type_id);
		if (!acl) return; // Skip if ACL type not found

		// User-based access (e.g., Read User = 204, Edit User = 202)
		if ((r.comp_type_id === 202 || r.comp_type_id === 204) && user?.id != null) {
			if (r.comp_value === user.id) {
				access = Math.max(access, acl.value);
				// debugLog(10, "[baselineAccess] User-based access", {
				// 	userId: user.id,
				// 	hasAccess: access,
				// 	baseline: item.id,
				// });
				return; // done for this row
			}
		}

		// Group-based access (if group data present)
		// if (!groups) return;

		const groupId = r.comp_value;
		// Handle special groups directly by id (no lookup dependency)
		if (groupId === GROUP_PUBLIC) {
			debugLog?.(4, "[baselineAccess] PUBLIC → READ", { baselineId: item.id });
			// PUBLIC / Everyone (works even if not logged in)
			access = Math.max(access, acl.value);
			return;
		}
		if (groupId === GROUP_ALL_LOGIN) {
			debugLog?.(4, "[baselineAccess] ALL_LOGIN", { baselineId: item.id, loggedIn });
			// ALL_LOGIN (requires login)
			if (loggedIn) access = Math.max(access, acl.value);
			return;
		}
		// Regular groups: requires groups list + membership check
		if (groups && user?.id != null) {
			const thisGroup = findObjectByProperty(groups, "id", groupId);
			if (thisGroup && isUserInGroup(thisGroup, user.id)) {
				access = Math.max(access, acl.value);
			}
		}
		// debugLog(10, "[baselineAccess] Group-based access", {
		// 	groups: groups,
		// 	group_id: r.comp_value,
		// 	userId: user.id,
		// 	hasAccess: access,
		// 	baseline: item.id,
		// });
	});
	return access;
}; */
